/* GS://PROTOCOLO
 * Arquivo: src/components/forms/AddUserForm.js
 * Dossiê de Projeto e Manual de Colaboração: Ecossistema GoldenSisters
 * Versão: 6.6 – Dados Financeiros de Colaboradores
 * Impacto da Versão: [src/components/forms/AddUserForm.js]
 * Data: 16/01/2026
 * Responsável: Gemini (conforme Protocolo)
 *
 * Mudanças desta versão:
 * - [ADD]: Seção "Dados Financeiros" (Comissão, Diária, PIX, Tipo de Remuneração).
 * - [LOGIC]: Envio desses novos campos no payload da função createUser.
 * - [UX]: Campos numéricos formatados para facilitar input.
 */

import React, { useState, useEffect } from 'react';
// Importa a função createUser do serviço de Firestore
import { createUser } from '../../services/firestoreService';

// --- Definindo as permissões disponíveis no sistema ---
const availablePermissions = [
  { key: 'viewDashboard', label: 'Ver Dashboard' },
  { key: 'accessPOS', label: 'Acessar Frente de Caixa (PDV)' },
  { key: 'manageOrders', label: 'Gerenciar Pedidos' },
  { key: 'manageProducts', label: 'Gerenciar Produtos e Categorias' },
  { key: 'manageSuppliers', label: 'Gerenciar Fornecedores' },
  { key: 'managePurchases', label: 'Gerenciar Compras' },
  { key: 'manageCustomers', label: 'Gerenciar Clientes' },
  { key: 'manageUsers', label: 'Gerenciar Usuários (Gestão RH)' },
  { key: 'accessFinanceiro', label: 'Acessar Módulo Financeiro' },
  { key: 'manageRentals', label: 'Gerenciar Aluguéis' },
];

// Função para criar o estado inicial das permissões (todas desmarcadas por padrão)
const createInitialPermissionsState = () => availablePermissions.reduce((acc, perm) => {
  acc[perm.key] = false;
  return acc;
}, {});

const AddUserForm = ({ onUserAdded }) => {
  const [displayName, setDisplayName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState('vendedor'); // Inicializa com 'vendedor'
  const [permissions, setPermissions] = useState(createInitialPermissionsState());

  // [GS-NEW]: Estados para Dados Financeiros
  const [remunerationType, setRemunerationType] = useState('comissao'); // comissao, diaria, hibrido
  const [dailyRate, setDailyRate] = useState(''); // Valor em R$
  const [commissionRate, setCommissionRate] = useState(''); // Valor em %
  const [pixKey, setPixKey] = useState('');

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // --- Lógica para preencher permissões padrão baseadas no papel (role) ---
  useEffect(() => {
    const newPermissions = createInitialPermissionsState();
    if (role === 'vendedor' || role === 'atendente') {
      newPermissions.accessPOS = true; 
      newPermissions.manageRentals = true;
      newPermissions.manageCustomers = true;
    }
    // GS-UPDATE: 'freelance' inicia manual (false) para autonomia total
    
    setPermissions(newPermissions);
  }, [role]);

  const handlePermissionChange = (e) => {
    const { name, checked } = e.target;
    setPermissions(prev => ({
      ...prev,
      [name]: checked
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    setSuccess('');

    if (!displayName || !email || !password) {
      setError('Por favor, preencha os campos de Nome, Email e Senha.');
      setIsLoading(false);
      return;
    }

    try {
      const result = await createUser({
        email,
        password,
        name: displayName,
        role,
        permissions,
        // [GS-NEW]: Enviando dados financeiros no payload
        remunerationType,
        dailyRate: parseFloat(dailyRate) || 0,
        commissionRate: parseFloat(commissionRate) || 0,
        pixKey
      });
      
      setSuccess(result.data.message);
      
      // Limpa o formulário
      setDisplayName('');
      setEmail('');
      setPassword('');
      setRole('vendedor'); 
      setPermissions(createInitialPermissionsState()); 
      
      // Limpa campos financeiros
      setRemunerationType('comissao');
      setDailyRate('');
      setCommissionRate('');
      setPixKey('');

      if (onUserAdded) {
        onUserAdded();
      }

    } catch (err) {
      console.error('Erro ao chamar a Cloud Function createUser:', err);
      setError(err.message || 'Erro desconhecido ao criar usuário.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="bg-white shadow-lg border border-gray-200 rounded-lg px-8 pt-6 pb-8 mb-4">
      <h2 className="text-xl font-bold mb-4 text-gray-800">Adicionar Novo Usuário</h2>

        {error && <p className="bg-red-50 border border-red-200 text-red-700 p-3 rounded mb-4 text-sm">{error}</p>}
        {success && <p className="bg-green-50 border border-green-200 text-green-700 p-3 rounded mb-4 text-sm">{success}</p>}
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <div>
            <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="displayName">Nome Completo</label>
            <input
            id="displayName"
            type="text"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded text-gray-900 focus:outline-none focus:ring-2 focus:ring-brand-gold focus:border-transparent"
            required
            />
        </div>
        <div>
            <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="email">Email</label>
            <input
            id="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded text-gray-900 focus:outline-none focus:ring-2 focus:ring-brand-gold focus:border-transparent"
            required
            />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <div>
            <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="password">Senha Provisória</label>
            <input
            id="password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded text-gray-900 focus:outline-none focus:ring-2 focus:ring-brand-gold focus:border-transparent"
            required
            />
        </div>
        <div>
            <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="role">Papel (Role)</label>
            <select
            id="role"
            value={role}
            onChange={(e) => setRole(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded text-gray-900 focus:outline-none focus:ring-2 focus:ring-brand-gold focus:border-transparent"
            required
            >
            <option value="vendedor">Vendedor</option>
            <option value="atendente">Atendente</option>
            <option value="freelance">Freelance (Temporário)</option>
            <option value="financeiro">Financeiro</option>
            <option value="gerente">Gerente</option>
            <option value="admin">Administrador (CEO)</option>
            </select>
        </div>
      </div>

      {/* [GS-NEW]: Seção de Dados Financeiros (Contratos) */}
      <div className="mb-6 bg-yellow-50 p-4 rounded-lg border border-yellow-200">
        <h3 className="text-sm font-bold text-yellow-800 mb-3 uppercase tracking-wide">Dados Financeiros (Contrato)</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
                <label className="block text-gray-700 text-xs font-bold mb-1" htmlFor="remunerationType">Modelo de Remuneração</label>
                <select
                    id="remunerationType"
                    value={remunerationType}
                    onChange={(e) => setRemunerationType(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-brand-gold"
                >
                    <option value="comissao">Comissão Pura (Vendas)</option>
                    <option value="diaria">Diária Fixa</option>
                    <option value="hibrido">Híbrido (Diária + Comissão)</option>
                </select>
            </div>

            <div>
                <label className="block text-gray-700 text-xs font-bold mb-1" htmlFor="pixKey">Chave PIX (Para Pagamentos)</label>
                <input
                    id="pixKey"
                    type="text"
                    value={pixKey}
                    onChange={(e) => setPixKey(e.target.value)}
                    placeholder="CPF, Email ou Aleatória"
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-brand-gold"
                />
            </div>

            {/* Renderização Condicional dos Valores */}
            {(remunerationType === 'diaria' || remunerationType === 'hibrido') && (
                <div>
                    <label className="block text-gray-700 text-xs font-bold mb-1" htmlFor="dailyRate">Valor da Diária (R$)</label>
                    <input
                        id="dailyRate"
                        type="number"
                        min="0"
                        step="0.01"
                        value={dailyRate}
                        onChange={(e) => setDailyRate(e.target.value)}
                        placeholder="Ex: 80.00"
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-brand-gold"
                    />
                </div>
            )}

            {(remunerationType === 'comissao' || remunerationType === 'hibrido') && (
                <div>
                    <label className="block text-gray-700 text-xs font-bold mb-1" htmlFor="commissionRate">% de Comissão</label>
                    <input
                        id="commissionRate"
                        type="number"
                        min="0"
                        step="0.1"
                        value={commissionRate}
                        onChange={(e) => setCommissionRate(e.target.value)}
                        placeholder="Ex: 5.0"
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-brand-gold"
                    />
                </div>
            )}
        </div>
      </div>

      {/* Renderizando os checkboxes de permissão */}
      <div className="mb-6">
        <label className="block text-gray-700 text-sm font-bold mb-2">Permissões de Acesso</label>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 bg-gray-50 p-4 rounded-md border border-gray-200">
          {availablePermissions.map((perm) => (
            <label key={perm.key} className="flex items-center space-x-3 cursor-pointer">
              <input
                type="checkbox"
                name={perm.key}
                checked={permissions[perm.key]}
                onChange={handlePermissionChange}
                className="h-4 w-4 rounded border-gray-300 text-yellow-600 focus:ring-yellow-500"
              />
              <span className="text-sm text-gray-700">{perm.label}</span>
            </label>
          ))}
        </div>
      </div>

      <button
        type="submit"
        disabled={isLoading}
        className="bg-brand-gold hover:bg-yellow-500 text-brand-black font-bold py-2.5 px-4 rounded-lg w-full disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm"
      >
        {isLoading ? 'Criando...' : 'Criar Usuário'}
      </button>
    </form>
  );
};

export default AddUserForm;